import { useState, useRef, useCallback } from 'react';
import { WavRecorder } from '../lib/wavtools/lib/wav_recorder';
import { transcribeAudio } from '../api/AudioApi';

const EMPTY_AUDIO = new Float32Array();

/**
 * Hook to handle audio transcription using Whisper API
 * @param {Object} config - Configuration object
 * @param {string} config.language - Language code for transcription ('es', 'en', 'pt')
 * @param {Function} config.onTranscriptionComplete - Callback when transcription completes
 * @param {Function} config.onTranscriptionStart - Callback when transcription starts
 * @param {Function} config.onTranscriptionEnd - Callback when transcription ends
 */
export const useTranscriber = ({
  language = 'es',
  onTranscriptionComplete,
  onTranscriptionStart,
  onTranscriptionEnd,
}) => {
  const wavRecorderRef = useRef(null);
  const animationFrameRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [audioData, setAudioData] = useState(EMPTY_AUDIO);
  const [errorCode, setErrorCode] = useState(null);

  const ensureRecorder = useCallback(() => {
    if (!wavRecorderRef.current) {
      wavRecorderRef.current = new WavRecorder({ sampleRate: 16000 });
    }
    return wavRecorderRef.current;
  }, []);

  const stopWaveAnimation = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const updateWaveData = useCallback(() => {
    try {
      if (wavRecorderRef.current) {
        const result = wavRecorderRef.current.getFrequencies('voice');
        setAudioData(result.values);
      }
      animationFrameRef.current = requestAnimationFrame(updateWaveData);
    } catch (error) {
      stopWaveAnimation();
    }
  }, [stopWaveAnimation]);

  const startRecording = useCallback(async () => {
    try {
      const recorder = ensureRecorder();
      await recorder.begin();
      await recorder.record();
      setErrorCode(null);
      setIsRecording(true);
      setAudioData(EMPTY_AUDIO);
      animationFrameRef.current = requestAnimationFrame(updateWaveData);
    } catch (error) {
      setIsRecording(false);
      setErrorCode('audioProcessingError');
      stopWaveAnimation();
    }
  }, [ensureRecorder, updateWaveData, stopWaveAnimation]);

  const stopRecording = useCallback(async () => {
    if (!isRecording || isTranscribing) {
      return;
    }

    setIsRecording(false);
    stopWaveAnimation();
    setIsTranscribing(true);
    onTranscriptionStart?.();

    try {
      const recorder = ensureRecorder();
      const audioFile = await recorder.end();

      const data = await transcribeAudio(audioFile.blob, language);

      // Handle structured error response
      if (!data.success) {
        setErrorCode('audioProcessingError');
        return;
      }

      const transcription = data.text;
      const durationSeconds = audioFile.audioBuffer?.duration || 0;

      if (transcription && transcription.trim()) {
        setErrorCode(null);
        onTranscriptionComplete?.(
          transcription.trim(),
          audioFile.blob,
          durationSeconds
        );
      } else {
        setErrorCode('transcriptionEmpty');
      }
    } catch (error) {
      setErrorCode('transcriptionError');
    } finally {
      setIsTranscribing(false);
      onTranscriptionEnd?.();
      setAudioData(EMPTY_AUDIO);
    }
  }, [
    ensureRecorder,
    isTranscribing,
    isRecording,
    stopWaveAnimation,
    onTranscriptionStart,
    onTranscriptionComplete,
    onTranscriptionEnd,
    language,
  ]);

  const cancelRecording = useCallback(async () => {
    if (!isRecording) return;

    setIsRecording(false);
    stopWaveAnimation();

    try {
      if (wavRecorderRef.current) {
        await wavRecorderRef.current.end();
      }
    } catch (error) {
      console.error('Error cancelling recording:', error);
    } finally {
      setAudioData(EMPTY_AUDIO);
      setErrorCode(null);
    }
  }, [isRecording, stopWaveAnimation]);

  const resetError = useCallback(() => {
    setErrorCode(null);
  }, []);

  return {
    isRecording,
    isTranscribing,
    audioData,
    errorCode,
    startRecording,
    stopRecording,
    cancelRecording,
    resetError,
  };
};

export default useTranscriber;
